import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { ExternalLink, RefreshCw } from 'lucide-react'
import { Modal, Select, useToast } from '../../../admin/primitives.js'
import { Checkbox, Field, SwitchRow, TextInput } from '../../../admin/form.js'
import { guardedClose, useSaveBar } from '../../../shell/editing.js'
import { useConfirm } from '../../../shell/confirm.js'
import { Link } from '../../../router.js'
import { fmtDateHeure, lireSupervision, type Lecture } from '../../api.js'
import { BandeauLecture, useVueSonde } from '../commun.js'
import { estPowerDoc } from '../batiment/batiment-modele.js'
import { onduleursRanges } from '../batiment/onduleurs-modele.js'
import { ecrireSonde, estConflitVersion, type Ecriture } from './ecrire.js'
import { LigneRetour, libelleSalle, optionsSalles, type Retour } from './elements.js'
import {
  LIBELLE_CRITICITE, pesePourUrgence, LIBELLE_CYCLE, LIBELLE_EXCEPTION, LIBELLE_FAMILLE, LIBELLE_LIEN, LIBELLE_STATUT_FENETRE, LIBELLE_SUIVI,
  LIBELLE_VISIBILITE, SCHEMAS_SAISISSABLES, UID_POSTE, acteur, brouillonDe, ecartPoste, estFicheEdition, estListeMaintenances,
  expliquerTemoin, libelleSchema, libelleSource, libelleType, problemePoste, SECTEUR_SEUL,
  type BrouillonPoste, type FenetreMaintenance, type FicheEdition, type LienRegistre, type SalleRegistre,
} from './modele.js'

/**
 * FICHE D'ÉDITION D'UN POSTE (21/10/2026) — `GET registry/devices/:uid`.
 *
 * Les VALEURS du poste (nom, salle, criticité, suivi…) passent par la Save Bar :
 * `PUT devices/:uid` avec l'ETag de la fiche en If-Match, seulement l'écart.
 * Fiche modifiée entre-temps (412 → 409 PRECONDITION_FAILED) : la fiche est
 * relue, la SAISIE EST GARDÉE, la barre le dit.
 *
 * Les GESTES (identifiant, fiche Parc, lien, exception, maintenance, retrait,
 * effacement) sont des boutons avec confirmation, jamais la Save Bar. La fiche
 * Parc se rattache UNIQUEMENT par `POST devices/:uid/parc`.
 */

type CleSection = 'remplace' | 'identifiants' | 'parc' | 'liens' | 'exceptions' | 'maintenances' | 'retrait'

const versSaisieLocale = (iso: string | null): string => {
  if (!iso) return ''
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}
const depuisSaisieLocale = (v: string): string | null => {
  if (!v) return null
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

const nomParc = (s: Record<string, unknown> | null): string | null => {
  if (!s) return null
  for (const k of ['nom', 'name', 'label', 'titre', 'designation']) if (typeof s[k] === 'string' && s[k]) return s[k] as string
  return null
}

function Section(props: { titre: string; compte?: string; retour?: Retour; children: ReactNode }) {
  return (
    <section className="sv-rgs-fiche__section">
      <h3 className="sv-rgs-fiche__titre">{props.titre}{props.compte ? <span className="sv-rgs-compte">{props.compte}</span> : null}</h3>
      {props.children}
      {props.retour ? <LigneRetour retour={props.retour} /> : null}
    </section>
  )
}

export function FicheAppareil(props: {
  uid: string; peutGerer: boolean; salles: SalleRegistre[] | null; onFermer: () => void; onChange: () => void
}) {
  const { uid, peutGerer } = props
  const toast = useToast()
  const confirm = useConfirm()
  const [lecture, setLecture] = useState<Lecture<FicheEdition> | null>(null)
  const [doc, setDoc] = useState<FicheEdition | null>(null)
  const [brouillon, setBrouillon] = useState<BrouillonPoste | null>(null)
  const [maintenances, setMaintenances] = useState<FenetreMaintenance[] | null>(null)
  // Les onduleurs proposés pour les alimentations A/B (lus seulement si le droit d'écrire est là).
  const power = useVueSonde(peutGerer ? 'power' : null, estPowerDoc, 300_000)
  const onduleurs = power.lecture?.etat === 'ok' ? onduleursRanges(power.lecture.data.ups) : power.precedente ? onduleursRanges(power.precedente.ups) : []
  const [occupe, setOccupe] = useState(false)
  const [retours, setRetours] = useState<Partial<Record<CleSection, Retour>>>({})
  const [historique, setHistorique] = useState(false)

  // Gestes : petites saisies locales (jamais dans la Save Bar).
  const [ident, setIdent] = useState({ scheme: 'mac', value: '', replace: false })
  const [parc, setParc] = useState('')
  const [lien, setLien] = useState({ kind: 'uplink', to: '', port: '', replace: true })
  const [exc, setExc] = useState({ kind: 'unplugged', reason: '', fin: '' })
  const [maint, setMaint] = useState({ debut: '', fin: '', reason: '' })
  const [motif, setMotif] = useState('')

  const brouillonRef = useRef<BrouillonPoste | null>(null)
  brouillonRef.current = brouillon
  const docRef = useRef<FicheEdition | null>(null)
  docRef.current = doc

  /**
   * Relit la fiche. `garder` conserve une saisie EN COURS (conflit de version,
   * geste fait pendant une édition) ; sans saisie en cours, le brouillon suit
   * la nouvelle fiche (sinon un retrait ferait apparaître une fausse modification).
   */
  const relire = useCallback(async (garder: boolean) => {
    const l = await lireSupervision(`/v/registry/devices/${encodeURIComponent(uid)}`, estFicheEdition)
    setLecture(l)
    if (l.etat !== 'ok') return null
    const ancien = docRef.current?.device ?? null
    const b = brouillonRef.current
    const enCours = ancien !== null && b !== null && Object.keys(ecartPoste(ancien, b)).length > 0
    setDoc(l.data)
    if (!garder || !enCours) setBrouillon(brouillonDe(l.data.device))
    return l.data
  }, [uid])

  const relireMaintenances = useCallback(async () => {
    const l = await lireSupervision(`/v/maintenance?scope_kind=device&scope_ref=${encodeURIComponent(uid)}`, estListeMaintenances)
    setMaintenances(l.etat === 'ok' ? l.data.windows : null)
  }, [uid])

  useEffect(() => { void relire(false); void relireMaintenances() }, [relire, relireMaintenances])

  const poste = doc?.device ?? null
  const ecart = poste && brouillon ? ecartPoste(poste, brouillon) : {}
  const nbEcarts = Object.keys(ecart).length
  const dirty = peutGerer && nbEcarts > 0

  useSaveBar({
    dirty,
    count: nbEcarts,
    invalid: brouillon ? problemePoste(brouillon) : null,
    saveLabel: 'Enregistrer le poste',
    onSave: async () => {
      if (!poste) return 'Fiche illisible pour l’instant : rien n’a été modifié.'
      const e = await ecrireSonde('PUT', `devices/${encodeURIComponent(poste.uid)}`, ecart, { etag: poste.etag })
      if (e.ok && estFicheEdition((e.data as { doc?: unknown } | null)?.doc)) {
        const d = (e.data as { doc: FicheEdition }).doc
        setDoc(d); setBrouillon(brouillonDe(d.device)); props.onChange()
        return true
      }
      if (e.ok) { await relire(false); props.onChange(); return true }
      if (estConflitVersion(e)) {
        await relire(true)
        return 'La fiche a été modifiée entre-temps dans Sonde : elle vient d’être relue et votre saisie est conservée. Vérifiez, puis enregistrez de nouveau.'
      }
      if (e.code === 'DEVICE_IS_POWER_ROOT') return expliquerTemoin(e.message)
      return e.message
    },
    onCancel: () => { if (poste) setBrouillon(brouillonDe(poste)) },
    successMessage: 'Poste enregistré dans Sonde.',
  })

  const fermer = () => guardedClose(dirty, props.onFermer)

  /** Un geste : écrit, dit le résultat dans sa section, relit la fiche sans perdre la saisie. */
  const geste = async (section: CleSection, fn: () => Promise<Ecriture>, succes: string, explique?: (e: Ecriture) => string | null) => {
    setOccupe(true)
    setRetours((r) => ({ ...r, [section]: null }))
    const e = await fn()
    setOccupe(false)
    if (!e.ok) {
      if (estConflitVersion(e)) {
        await relire(true)
        setRetours((r) => ({ ...r, [section]: { ton: 'ko', texte: 'La fiche a été modifiée entre-temps : elle vient d’être relue. Vérifiez, puis recommencez.' } }))
        return e
      }
      const t = e.code === 'DEVICE_IS_POWER_ROOT' ? expliquerTemoin(e.message) : explique?.(e) ?? e.message
      setRetours((r) => ({ ...r, [section]: { ton: 'ko', texte: t } }))
      return e
    }
    toast('ok', succes)
    await relire(true)
    props.onChange()
    return e
  }

  if (!doc || !poste || !brouillon) {
    return (
      <Modal open onClose={props.onFermer} ariaLabel={`Fiche du poste ${uid}`} width={980}>
        <div className="lc-modal__head"><h2 className="lc-modal__title">Poste « {uid} »</h2></div>
        <div className="lc-modal__body">
          {lecture === null ? <p className="sv-rgs-aide" role="status">Lecture de la fiche…</p> : <BandeauLecture lecture={lecture} />}
        </div>
      </Modal>
    )
  }

  const retire = poste.lifecycle === 'retired'
  const courants = doc.identifiers.filter((i) => i.current)
  const clos = doc.identifiers.filter((i) => !i.current)
  const parcConfirmes = doc.parc_links.filter((p) => p.origin === 'confirmed' && p.valid_to === null)
  const parcSuggeres = doc.parc_links.filter((p) => p.origin === 'suggested' && p.valid_to === null)
  const exceptions = doc.overrides.filter((o) => o.status === 'active' || o.status === 'scheduled')
  const maintEnCours = (maintenances ?? []).filter((m) => m.status === 'active' || m.status === 'scheduled')
  const optsSalles = optionsSalles(props.salles, [brouillon.zone_code])
  const optsCycle = (retire ? ['retired', 'active'] : ['active', 'spare', 'ignored']).map((c) => ({ value: c, label: c === 'active' && retire ? 'Réactiver (actif)' : LIBELLE_CYCLE[c] ?? c }))
  const ro = !peutGerer
  const alimsConnues = poste.power_feeds !== undefined
  const nomOnduleur = (v: string) => v === SECTEUR_SEUL ? 'Secteur seul (non ondulée)' : onduleurs.find((u) => u.device.uid === v)?.device.display_name ?? v
  const optsAlim = (actuelle: string, b: boolean) => [
    { value: '', label: b ? 'Aucune (une seule alimentation)' : 'Non renseignée' },
    { value: SECTEUR_SEUL, label: 'Secteur seul (non ondulée)' },
    ...onduleurs.map((u) => ({ value: u.device.uid, label: u.device.display_name })),
    ...(actuelle && actuelle !== SECTEUR_SEUL && !onduleurs.some((u) => u.device.uid === actuelle) ? [{ value: actuelle, label: actuelle }] : []),
  ]
  const texteAlims = (a: string, b: string) => !a ? '—' : `A : ${nomOnduleur(a)}${b ? ` · B : ${nomOnduleur(b)}` : ''}`

  const ligneLien = (l: LienRegistre, sens: 'amont' | 'aval') => {
    const autre = sens === 'amont' ? { uid: l.to_uid, nom: l.to_name } : { uid: l.from_uid, nom: l.from_name }
    const port = l.attrs['port'] ?? l.attrs['to_port']
    return (
      <li key={l.id} className="sv-rgs-item">
        <span className="sv-rgs-item__corps">
          <span>{l.kind_label || LIBELLE_LIEN[l.kind] || l.kind} : <Link href={`/infrastructure/appareils/${encodeURIComponent(autre.uid)}`}>{autre.nom}</Link>
            {typeof port === 'string' || typeof port === 'number' ? ` · port ${port}` : ''}</span>
          <span className="sv-rgs-meta">
            <span>{l.origin === 'auto' ? `Appris (${libelleSource(l.source)})` : 'Déclaré'}</span>
            <span>Confiance {l.confidence} %</span>
            <span>Depuis le {fmtDateHeure(l.valid_from)}</span>
            {l.confirmed_by ? <span>Confirmé par {acteur(l.confirmed_by)}</span> : null}
          </span>
        </span>
        {!ro && l.current && !retire ? (
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" disabled={occupe} onClick={async () => {
            if (!await confirm({ title: 'Clore ce lien ?', message: 'Sonde ne s’en servira plus pour expliquer une panne par l’amont. L’historique reste.', confirmLabel: 'Clore le lien' })) return
            await geste('liens', () => ecrireSonde('DELETE', `links/${encodeURIComponent(l.id)}`), 'Lien clos.')
          }}>Clore</button>
        ) : null}
      </li>
    )
  }

  return (
    <Modal open onClose={fermer} ariaLabel={`Fiche du poste ${poste.display_name}`} width={980} className="lc-modal--editable">
      <div className="lc-modal__head sv-rgs-fiche__tete">
        <div className="sv-rgs-fiche__ident">
          <h2 className="lc-modal__title">{poste.display_name}</h2>
          <p className="sv-rgs-meta">
            <span className="sv-mono">{poste.uid}</span>
            <span>{libelleType(poste.kind)} · {LIBELLE_FAMILLE[poste.family] ?? poste.family}</span>
            <span className="sv-rgs-cycle">{LIBELLE_CYCLE[poste.lifecycle] ?? poste.lifecycle}</span>
            <span>Modifié le {fmtDateHeure(poste.updated_at)}</span>
          </p>
        </div>
        <div className="sv-rgs-fiche__liens">
          <Link className="lc-btn lc-btn--ghost lc-btn--sm" href={`/infrastructure/appareils/${encodeURIComponent(poste.uid)}`}>
            <ExternalLink size={14} aria-hidden="true" /> Supervision
          </Link>
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => { void relire(true); void relireMaintenances() }}>
            <RefreshCw size={14} aria-hidden="true" /> Relire
          </button>
        </div>
      </div>
      <div className="lc-modal__body sv-rgs-fiche">
        {lecture && lecture.etat !== 'ok' ? <BandeauLecture lecture={lecture} /> : null}
        {ro ? <p className="sv-rgs-lecture-seule" role="note">Lecture seule : modifier le registre demande le droit « Gérer le registre de la supervision ».</p> : null}

        {/* ─── Le poste : des VALEURS, Save Bar ─── */}
        <Section titre="Poste">
          {ro ? (
            <dl className="sv-rgs-faits">
              <div><dt>Salle</dt><dd>{libelleSalle(props.salles, poste.zone_code)}{poste.zone_detail ? ` · ${poste.zone_detail}` : ''}</dd></div>
              <div><dt>Urgence</dt><dd>{LIBELLE_CRITICITE[poste.criticality] ?? poste.criticality}</dd></div>
              <div><dt>Pèse dans l’état global</dt><dd>{poste.affects_global_health ? 'Oui' : 'Non'}</dd></div>
              <div><dt>Suivi</dt><dd>{LIBELLE_SUIVI[poste.tracking] ?? poste.tracking}</dd></div>
              <div><dt>Visibilité</dt><dd>{LIBELLE_VISIBILITE[poste.visibility] ?? poste.visibility}</dd></div>
              <div><dt>Domaine d’alimentation</dt><dd>{poste.power_domain_code ?? '—'}</dd></div>
              {alimsConnues ? <div><dt>Alimentations</dt><dd>{texteAlims(brouillon.alim_a, brouillon.alim_b)}</dd></div> : null}
              <div><dt>Notes</dt><dd>{poste.notes ?? '—'}</dd></div>
            </dl>
          ) : (
            <div className="sv-rgs-form">
              <Field label="Nom affiché">
                <TextInput value={brouillon.display_name} maxLength={200} ariaLabel="Nom affiché" onChange={(v) => setBrouillon({ ...brouillon, display_name: v })} />
              </Field>
              <Field label="Salle" helper="Le poste s’affiche dans cette salle et est jugé avec elle (éteinte comme prévu ≠ panne).">
                <Select value={brouillon.zone_code ?? ''} options={optsSalles} ariaLabel="Salle" onChange={(v) => setBrouillon({ ...brouillon, zone_code: v || null })} />
              </Field>
              <Field label="Détail d’emplacement" helper="Facultatif : « au-dessus du bar », « baie, U12 »…">
                <TextInput value={brouillon.zone_detail} maxLength={200} ariaLabel="Détail d’emplacement" onChange={(v) => setBrouillon({ ...brouillon, zone_detail: v })} />
              </Field>
              <Field label="Urgence" helper="Décide des délais d’alerte : « Critique en soirée » prévient le plus vite pendant l’ouverture ; « Confort » et « Information » ne font pas baisser l’état général (réglable juste en dessous).">
                <Select value={brouillon.criticality} ariaLabel="Urgence" onChange={(v) => setBrouillon({ ...brouillon, criticality: v, affects_global_health: pesePourUrgence(v) })}
                  options={Object.entries(LIBELLE_CRITICITE).map(([value, label]) => ({ value, label }))} />
              </Field>
              <Field label="Suivi">
                <Select value={brouillon.tracking} ariaLabel="Suivi" onChange={(v) => setBrouillon({ ...brouillon, tracking: v })}
                  options={Object.entries(LIBELLE_SUIVI).map(([value, label]) => ({ value, label }))} />
              </Field>
              <Field label="Visibilité">
                <Select value={brouillon.visibility} ariaLabel="Visibilité" onChange={(v) => setBrouillon({ ...brouillon, visibility: v })}
                  options={Object.entries(LIBELLE_VISIBILITE).map(([value, label]) => ({ value, label }))} />
              </Field>
              <Field label="Cycle de vie" helper={retire ? `Retiré le ${fmtDateHeure(poste.retired_at)} : le réactiver le remet sous surveillance.` : 'En réserve ou ignoré : plus surveillé, l’historique reste.'}>
                <Select value={brouillon.lifecycle} options={optsCycle} ariaLabel="Cycle de vie" onChange={(v) => setBrouillon({ ...brouillon, lifecycle: v })} />
              </Field>
              <div className="sv-rgs-form__large">
                <SwitchRow checked={brouillon.affects_global_health} onChange={(v) => setBrouillon({ ...brouillon, affects_global_health: v })}
                  title="Pèse dans l’état global" desc="Sa panne fait passer le club en « Attention » ou « Dégradé »." />
              </div>
              <div className="sv-rgs-form__large">
                <Field label="Notes">
                  <textarea className="lc-input sv-rgs-texte" rows={3} maxLength={2000} value={brouillon.notes} aria-label="Notes"
                    onChange={(e) => setBrouillon({ ...brouillon, notes: e.target.value })} />
                </Field>
              </div>
              {alimsConnues ? (
                <>
                  <Field label="Alimentation A" helper="L’onduleur qui alimente l’appareil, ou « Secteur seul » s’il est branché au mur. Sonde en tire son autonomie si le courant coupe.">
                    <Select value={brouillon.alim_a} options={optsAlim(brouillon.alim_a, false)} ariaLabel="Alimentation A" onChange={(v) => setBrouillon({ ...brouillon, alim_a: v, alim_b: v ? brouillon.alim_b : '' })} />
                  </Field>
                  {brouillon.alim_a ? (
                    <Field label="Alimentation B" helper="Seulement pour un appareil à deux alimentations (Dell, OneAccess…). Une alimentation sur secteur seul ne compte pas pendant une coupure.">
                      <Select value={brouillon.alim_b} options={optsAlim(brouillon.alim_b, true)} ariaLabel="Alimentation B" onChange={(v) => setBrouillon({ ...brouillon, alim_b: v })} />
                    </Field>
                  ) : null}
                </>
              ) : null}
              <p className="sv-rgs-aide sv-rgs-form__large">Domaine d’alimentation : {poste.power_domain_code ?? '—'} (se règle dans Salles et alimentation).</p>
            </div>
          )}
        </Section>

        {/* ─── Exemplaire remplacé ? ─── */}
        {doc.pending_discoveries.length > 0 ? (
          <Section titre="Exemplaire remplacé ?" compte={String(doc.pending_discoveries.length)} retour={retours.remplace ?? null}>
            <ul className="sv-rgs-items">
              {doc.pending_discoveries.map((p) => (
                <li key={p.id} className="sv-rgs-item">
                  <span className="sv-rgs-item__corps">
                    <span><span className="sv-mono">{libelleSchema(p.scheme)} · {p.value}</span></span>
                    <span className="sv-rgs-aide">{p.reason_fr} Vu le {fmtDateHeure(p.last_seen_at)}.</span>
                  </span>
                  {!ro && !retire ? (
                    <button type="button" className="lc-btn lc-btn--sm" disabled={occupe} onClick={async () => {
                      if (!await confirm({
                        title: 'Rattacher en remplaçant ?', variant: 'sensitive', confirmLabel: 'Rattacher en remplaçant',
                        message: 'Le nouvel identifiant est rattaché à ce poste et l’ancien du même genre est clos : l’historique suit le poste.',
                      })) return
                      await geste('remplace', () => ecrireSonde('POST', `discovery/${encodeURIComponent(p.id)}/attach`, { uid: poste.uid, replace: true }), 'Exemplaire remplacé : nouvel identifiant rattaché, l’ancien est clos.')
                    }}>Rattacher en remplaçant</button>
                  ) : null}
                </li>
              ))}
            </ul>
          </Section>
        ) : null}

        {/* ─── Identifiants ─── */}
        <Section titre="Identifiants" compte={String(courants.length)} retour={retours.identifiants ?? null}>
          {courants.length === 0 ? <p className="sv-rgs-aide">Aucun identifiant en cours : Sonde ne peut pas reconnaître cet appareil sur le réseau.</p> : null}
          <ul className="sv-rgs-items">
            {courants.map((i) => (
              <li key={i.id} className="sv-rgs-item">
                <span className="sv-rgs-item__corps">
                  <span>{libelleSchema(i.scheme)} · <span className="sv-mono">{i.value}</span>{i.vlan !== null ? ` · VLAN ${i.vlan}` : ''}</span>
                  <span className="sv-rgs-meta">
                    <span>Depuis le {fmtDateHeure(i.valid_from)}</span>
                    <span>{libelleSource(i.source)}</span>
                    <span>Confiance {i.confidence} %</span>
                    {i.confirmed_by ? <span>Confirmé par {acteur(i.confirmed_by)}</span> : null}
                  </span>
                </span>
                {!ro ? (
                  <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" disabled={occupe} onClick={async () => {
                    if (!await confirm({
                      title: 'Clore cet identifiant ?', confirmLabel: 'Clore',
                      message: i.scheme === 'parc_item_id'
                        ? 'La fiche Parc est détachée de ce poste. L’historique reste.'
                        : 'Sonde ne reconnaîtra plus l’appareil par cet identifiant : s’il réapparaît, il reviendra dans « À valider ». L’historique reste.',
                      details: [{ label: libelleSchema(i.scheme), value: i.value }],
                    })) return
                    await geste('identifiants', () => ecrireSonde('DELETE', `identifiers/${encodeURIComponent(i.id)}`, {}), 'Identifiant clos.')
                  }}>Clore</button>
                ) : null}
              </li>
            ))}
          </ul>
          {clos.length > 0 ? (
            <>
              <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" aria-expanded={historique} onClick={() => setHistorique(!historique)}>
                {historique ? 'Masquer l’historique' : `Historique (${clos.length} clos)`}
              </button>
              {historique ? (
                <ul className="sv-rgs-items sv-rgs-items--passe">
                  {clos.map((i) => (
                    <li key={i.id} className="sv-rgs-item">
                      <span className="sv-rgs-item__corps">
                        <span>{libelleSchema(i.scheme)} · <span className="sv-mono">{i.value}</span></span>
                        <span className="sv-rgs-aide">Du {fmtDateHeure(i.valid_from)} au {fmtDateHeure(i.valid_to)}{i.confirmed_by ? ` · ${acteur(i.confirmed_by)}` : ''}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          ) : null}
          {!ro && !retire ? (
            <div className="sv-rgs-ajout">
              <Field label="Genre">
                <Select value={ident.scheme} ariaLabel="Genre d’identifiant" onChange={(v) => setIdent({ ...ident, scheme: v })}
                  options={SCHEMAS_SAISISSABLES.map((s) => ({ value: s, label: libelleSchema(s) }))} />
              </Field>
              <Field label="Valeur">
                <TextInput value={ident.value} maxLength={300} ariaLabel="Valeur de l’identifiant" onChange={(v) => setIdent({ ...ident, value: v })} />
              </Field>
              <div className="sv-rgs-ajout__fin">
                <Checkbox checked={ident.replace} onChange={(v) => setIdent({ ...ident, replace: v })} label="Exemplaire remplacé : clore l’identifiant du même genre en cours" />
                <button type="button" className="lc-btn lc-btn--sm" disabled={occupe || !ident.value.trim()} onClick={async () => {
                  const corps = { device_uid: poste.uid, scheme: ident.scheme, value: ident.value.trim(), ...(ident.replace ? { replace: true } : {}) }
                  const e = await geste('identifiants', () => ecrireSonde('POST', 'identifiers', corps), 'Identifiant rattaché.',
                    (x) => (x.code === 'IDENTIFIER_IN_USE' ? null : x.message))
                  if (e.ok) { setIdent({ ...ident, value: '', replace: false }); return }
                  if (e.code === 'IDENTIFIER_IN_USE') {
                    const ailleurs = typeof e.details?.['held_by'] === 'string' ? e.details['held_by'] : null
                    if (await confirm({
                      title: 'Déplacer cet identifiant ici ?', variant: 'sensitive', confirmLabel: 'Déplacer',
                      message: `${e.message} Le déplacer le clôt sur l’autre poste et l’ouvre sur celui-ci.`,
                      ...(ailleurs ? { details: [{ label: 'Rattaché à', value: ailleurs }] } : {}),
                    })) {
                      const m = await geste('identifiants', () => ecrireSonde('POST', 'identifiers', { ...corps, move: true }), 'Identifiant déplacé sur ce poste.')
                      if (m.ok) setIdent({ ...ident, value: '', replace: false })
                    }
                  }
                }}>Rattacher</button>
              </div>
            </div>
          ) : null}
        </Section>

        {/* ─── Fiche Parc : une seule voie, POST devices/:uid/parc ─── */}
        <Section titre="Fiche Parc" retour={retours.parc ?? null}>
          {parcConfirmes.length === 0 && parcSuggeres.length === 0 ? <p className="sv-rgs-aide">Aucune fiche Parc rattachée.</p> : null}
          <ul className="sv-rgs-items">
            {parcConfirmes.map((p) => (
              <li key={p.id} className="sv-rgs-item">
                <span className="sv-rgs-item__corps">
                  <span>Fiche <span className="sv-mono">{p.parc_item_id}</span>{nomParc(p.snapshot) ? ` · ${nomParc(p.snapshot)}` : ''}</span>
                  <span className="sv-rgs-aide">Confirmée par {acteur(p.confirmed_by)} le {fmtDateHeure(p.confirmed_at)} · l’état fait foi dans Parc.</span>
                </span>
                {!ro && !retire ? (
                  <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" disabled={occupe} onClick={async () => {
                    if (!await confirm({ title: 'Détacher la fiche Parc ?', message: 'Le poste ne sera plus relié à cet exemplaire de Parc ; les signalements proposés ne le citeront plus.', confirmLabel: 'Détacher' })) return
                    await geste('parc', () => ecrireSonde('POST', `devices/${encodeURIComponent(poste.uid)}/parc`, { parc_item_id: p.parc_item_id, decision: 'unlink' }), 'Fiche Parc détachée.')
                  }}>Détacher</button>
                ) : null}
              </li>
            ))}
            {parcSuggeres.map((p) => (
              <li key={p.id} className="sv-rgs-item">
                <span className="sv-rgs-item__corps">
                  <span>Suggestion : fiche <span className="sv-mono">{p.parc_item_id}</span>{nomParc(p.snapshot) ? ` · ${nomParc(p.snapshot)}` : ''}</span>
                  <span className="sv-rgs-aide">Confiance {p.confidence === null ? '—' : `${p.confidence} %`} : à confirmer par un humain.</span>
                </span>
                {!ro && !retire ? (
                  <span className="sv-rgs-item__actions">
                    <button type="button" className="lc-btn lc-btn--sm" disabled={occupe}
                      onClick={() => void geste('parc', () => ecrireSonde('POST', `devices/${encodeURIComponent(poste.uid)}/parc`, { parc_item_id: p.parc_item_id, decision: 'confirm' }), 'Fiche Parc rattachée.')}>Confirmer</button>
                    <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" disabled={occupe}
                      onClick={() => void geste('parc', () => ecrireSonde('POST', `devices/${encodeURIComponent(poste.uid)}/parc`, { parc_item_id: p.parc_item_id, decision: 'reject' }), 'Suggestion rejetée.')}>Rejeter</button>
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
          {!ro && !retire ? (
            <div className="sv-rgs-ajout sv-rgs-ajout--court">
              <Field label="Numéro de la fiche Parc" helper="Un exemplaire n’occupe qu’un poste à la fois.">
                <TextInput value={parc} maxLength={64} ariaLabel="Numéro de la fiche Parc" onChange={setParc} />
              </Field>
              <div className="sv-rgs-ajout__fin">
                <button type="button" className="lc-btn lc-btn--sm" disabled={occupe || !/^[A-Za-z0-9._:-]{1,64}$/.test(parc.trim())} onClick={async () => {
                  const e = await geste('parc', () => ecrireSonde('POST', `devices/${encodeURIComponent(poste.uid)}/parc`, { parc_item_id: parc.trim(), decision: 'confirm' }), 'Fiche Parc rattachée.')
                  if (e.ok) setParc('')
                }}>Rattacher la fiche</button>
              </div>
            </div>
          ) : null}
        </Section>

        {/* ─── Liens ─── */}
        <Section titre="Liens" compte={String(doc.links.upstream.filter((l) => l.current).length + doc.links.downstream.filter((l) => l.current).length)} retour={retours.liens ?? null}>
          <p className="sv-rgs-sous-titre">En amont (ce dont il dépend)</p>
          {doc.links.upstream.filter((l) => l.current).length === 0 ? <p className="sv-rgs-aide">Aucun amont connu : une panne de son switch ou de sa borne ne pourra pas être expliquée.</p> : null}
          <ul className="sv-rgs-items">{doc.links.upstream.filter((l) => l.current).map((l) => ligneLien(l, 'amont'))}</ul>
          {doc.links.downstream.filter((l) => l.current).length > 0 ? (
            <>
              <p className="sv-rgs-sous-titre">En aval (ce qui dépend de lui)</p>
              <ul className="sv-rgs-items">{doc.links.downstream.filter((l) => l.current).map((l) => ligneLien(l, 'aval'))}</ul>
            </>
          ) : null}
          {!ro && !retire ? (
            <div className="sv-rgs-ajout">
              <Field label="Genre de lien">
                <Select value={lien.kind} ariaLabel="Genre de lien" onChange={(v) => setLien({ ...lien, kind: v })}
                  options={Object.entries(LIBELLE_LIEN).map(([value, label]) => ({ value, label }))} />
              </Field>
              <Field label="Poste en amont (identifiant)">
                <TextInput value={lien.to} maxLength={63} ariaLabel="Identifiant du poste en amont" placeholder="sw-001-salon" onChange={(v) => setLien({ ...lien, to: v.toLowerCase() })} />
              </Field>
              <Field label="Port (facultatif)">
                <TextInput value={lien.port} maxLength={20} ariaLabel="Port en amont" onChange={(v) => setLien({ ...lien, port: v })} />
              </Field>
              <div className="sv-rgs-ajout__fin">
                <Checkbox checked={lien.replace} onChange={(v) => setLien({ ...lien, replace: v })} label="Remplace l’amont déclaré du même genre" />
                <button type="button" className="lc-btn lc-btn--sm" disabled={occupe || !UID_POSTE.test(lien.to) || lien.to === poste.uid} onClick={async () => {
                  const port = lien.port.trim()
                  const attrs = port ? { port: /^\d+$/.test(port) ? Number(port) : port } : undefined
                  const e = await geste('liens', () => ecrireSonde('POST', 'links', {
                    kind: lien.kind, from_uid: poste.uid, to_uid: lien.to, ...(attrs ? { attrs } : {}), ...(lien.replace ? { replace: true } : {}),
                  }), 'Lien déclaré.')
                  if (e.ok) setLien({ ...lien, to: '', port: '' })
                }}>Déclarer le lien</button>
              </div>
            </div>
          ) : null}
        </Section>

        {/* ─── Exceptions ─── */}
        <Section titre="Exceptions" compte={String(exceptions.length)} retour={retours.exceptions ?? null}>
          <p className="sv-rgs-aide">« Débranché volontairement », « éteint comme prévu »… : Sonde n’y voit pas une panne jusqu’à l’échéance.</p>
          <ul className="sv-rgs-items">
            {exceptions.map((o) => (
              <li key={o.id} className="sv-rgs-item">
                <span className="sv-rgs-item__corps">
                  <span>{o.kind_label || LIBELLE_EXCEPTION[o.kind] || o.kind} — {o.reason}</span>
                  <span className="sv-rgs-meta">
                    <span>{LIBELLE_STATUT_FENETRE[o.status] ?? o.status}</span>
                    <span>Du {fmtDateHeure(o.starts_at)} au {fmtDateHeure(o.ends_at)}</span>
                    <span>Par {acteur(o.created_by)}</span>
                  </span>
                </span>
                {!ro ? (
                  <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" disabled={occupe} onClick={async () => {
                    if (!await confirm({ title: 'Annuler cette exception ?', message: 'Sonde jugera de nouveau l’appareil normalement : s’il est absent, il sera vu en panne.', confirmLabel: 'Annuler l’exception' })) return
                    await geste('exceptions', () => ecrireSonde('DELETE', `overrides/${encodeURIComponent(o.id)}`), 'Exception annulée.')
                  }}>Annuler</button>
                ) : null}
              </li>
            ))}
          </ul>
          {!ro && !retire ? (
            <div className="sv-rgs-ajout">
              <Field label="Exception">
                <Select value={exc.kind} ariaLabel="Genre d’exception" onChange={(v) => setExc({ ...exc, kind: v })}
                  options={Object.entries(LIBELLE_EXCEPTION).map(([value, label]) => ({ value, label }))} />
              </Field>
              <Field label="Motif">
                <TextInput value={exc.reason} maxLength={500} ariaLabel="Motif de l’exception" placeholder="Prêté à l’association" onChange={(v) => setExc({ ...exc, reason: v })} />
              </Field>
              <Field label="Jusqu’au (heure de Paris)" helper={exc.kind === 'unplugged' ? 'Vide : 24 h par défaut. 90 jours au plus.' : 'Obligatoire, 90 jours au plus.'}>
                <input type="datetime-local" className="lc-input" value={exc.fin} aria-label="Échéance de l’exception" onChange={(e) => setExc({ ...exc, fin: e.target.value })} />
              </Field>
              <div className="sv-rgs-ajout__fin">
                <button type="button" className="lc-btn lc-btn--sm" disabled={occupe || !exc.reason.trim() || (exc.kind !== 'unplugged' && !depuisSaisieLocale(exc.fin))} onClick={async () => {
                  const fin = depuisSaisieLocale(exc.fin)
                  const e = await geste('exceptions', () => ecrireSonde('POST', 'overrides', {
                    device_uid: poste.uid, kind: exc.kind, reason: exc.reason.trim(), ...(fin ? { ends_at: fin } : {}),
                  }), 'Exception enregistrée.')
                  if (e.ok) setExc({ ...exc, reason: '', fin: '' })
                }}>Ajouter l’exception</button>
              </div>
            </div>
          ) : null}
        </Section>

        {/* ─── Maintenances ─── */}
        <Section titre="Maintenances" compte={maintenances === null ? '—' : String(maintEnCours.length)} retour={retours.maintenances ?? null}>
          {maintenances === null ? <p className="sv-rgs-aide">Fenêtres de maintenance illisibles pour l’instant.</p> : null}
          {maintenances !== null && maintEnCours.length === 0 ? <p className="sv-rgs-aide">Aucune maintenance prévue pour ce poste.</p> : null}
          <ul className="sv-rgs-items">
            {maintEnCours.map((m) => (
              <li key={m.id} className="sv-rgs-item">
                <span className="sv-rgs-item__corps">
                  <span>{m.reason}</span>
                  <span className="sv-rgs-meta">
                    <span>{LIBELLE_STATUT_FENETRE[m.status] ?? m.status}</span>
                    <span>Du {fmtDateHeure(m.starts_at)} au {fmtDateHeure(m.ends_at)}</span>
                    <span>Par {acteur(m.created_by)}</span>
                  </span>
                </span>
                {!ro ? (
                  <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" disabled={occupe} onClick={async () => {
                    if (!await confirm({ title: 'Annuler cette maintenance ?', message: 'Les alertes de ce poste reprennent aussitôt.', confirmLabel: 'Annuler la maintenance' })) return
                    const e = await geste('maintenances', () => ecrireSonde('DELETE', `maintenance/${encodeURIComponent(m.id)}`), 'Maintenance annulée.')
                    if (e.ok) void relireMaintenances()
                  }}>Annuler</button>
                ) : null}
              </li>
            ))}
          </ul>
          {!ro && !retire ? (
            <div className="sv-rgs-ajout">
              <Field label="Début (heure de Paris)">
                <input type="datetime-local" className="lc-input" value={maint.debut} aria-label="Début de la maintenance" onChange={(e) => setMaint({ ...maint, debut: e.target.value })} />
              </Field>
              <Field label="Fin" helper="7 jours au plus.">
                <input type="datetime-local" className="lc-input" value={maint.fin} aria-label="Fin de la maintenance" onChange={(e) => setMaint({ ...maint, fin: e.target.value })} />
              </Field>
              <Field label="Motif">
                <TextInput value={maint.reason} maxLength={500} ariaLabel="Motif de la maintenance" placeholder="Remplacement du switch" onChange={(v) => setMaint({ ...maint, reason: v })} />
              </Field>
              <div className="sv-rgs-ajout__fin">
                <button type="button" className="lc-btn lc-btn--sm"
                  disabled={occupe || !maint.reason.trim() || !depuisSaisieLocale(maint.debut) || !depuisSaisieLocale(maint.fin)} onClick={async () => {
                    const e = await geste('maintenances', () => ecrireSonde('POST', 'maintenance', {
                      scope_kind: 'device', scope_ref: poste.uid, starts_at: depuisSaisieLocale(maint.debut), ends_at: depuisSaisieLocale(maint.fin), reason: maint.reason.trim(),
                    }), 'Maintenance prévue : pas d’alerte pour ce poste pendant la fenêtre.')
                    if (e.ok) { setMaint({ debut: '', fin: '', reason: '' }); void relireMaintenances() }
                  }}>Prévoir la maintenance</button>
              </div>
              {!maint.debut ? (
                <p className="sv-rgs-aide sv-rgs-form__large">
                  <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => {
                    const d = new Date(); const f = new Date(d.getTime() + 2 * 3_600_000)
                    setMaint({ ...maint, debut: versSaisieLocale(d.toISOString()), fin: versSaisieLocale(f.toISOString()) })
                  }}>Maintenant, pour 2 h</button>
                </p>
              ) : null}
            </div>
          ) : null}
        </Section>

        {/* ─── Retrait / effacement ─── */}
        {!ro && !retire ? (
          <Section titre="Retirer le poste" retour={retours.retrait ?? null}>
            <p className="sv-rgs-aide">
              Retirer : le poste quitte les salles et n’est plus surveillé ; ses identifiants, liens et fiche Parc sont clos, son historique reste.
              Effacer définitivement : seulement un poste créé par erreur, sans aucun historique (confirmation forte).
            </p>
            <div className="sv-rgs-ajout sv-rgs-ajout--court">
              <Field label="Motif (obligatoire)">
                <TextInput value={motif} maxLength={500} ariaLabel="Motif du retrait" placeholder="Appareil déposé" onChange={setMotif} />
              </Field>
              <div className="sv-rgs-ajout__fin">
                <button type="button" className="lc-btn lc-btn--danger lc-btn--sm" disabled={occupe || !motif.trim()} onClick={async () => {
                  if (!await confirm({
                    title: `Retirer « ${poste.display_name} » ?`, variant: 'critical', confirmLabel: 'Retirer le poste',
                    message: 'Il ne sera plus surveillé ni affiché dans sa salle. Identifiants, liens et fiche Parc sont clos ; l’historique reste.',
                    details: [{ label: 'Motif', value: motif.trim() }],
                  })) return
                  const e = await geste('retrait', () => ecrireSonde('DELETE', `devices/${encodeURIComponent(poste.uid)}?reason=${encodeURIComponent(motif.trim())}`,
                    { reason: motif.trim() }, { etag: poste.etag }), 'Poste retiré : il n’est plus surveillé.')
                  if (e.ok) setMotif('')
                }}>Retirer</button>
                <button type="button" className="lc-btn lc-btn--danger-text lc-btn--sm" disabled={occupe || !motif.trim()} onClick={async () => {
                  if (!await confirm({
                    title: `Effacer « ${poste.uid} » définitivement ?`, variant: 'critical', confirmLabel: 'Effacer définitivement',
                    message: 'Seulement pour un poste créé par erreur : s’il a le moindre historique (états, incidents, alertes, annotations), Sonde refusera et il faudra le retirer. Votre identité sera redemandée.',
                  })) return
                  const e = await geste('retrait', () => ecrireSonde('POST', `devices/${encodeURIComponent(poste.uid)}/purge`, { reason: motif.trim() }),
                    'Poste effacé.', (x) => (x.code === 'DEVICE_HAS_HISTORY' ? `${x.message} Utilisez « Retirer ».` : null))
                  if (e.ok) props.onFermer()
                }}>Effacer définitivement</button>
              </div>
            </div>
          </Section>
        ) : null}
      </div>
    </Modal>
  )
}
